import {
  createPath,
  matchRoutes,
  Path,
  useLocation,
  useNavigate,
  useNavigation,
  useParams,
  useResolvedPath,
} from "react-router-dom";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LS2PacRouteObject, routes } from "../App";
import { useSessionStorageState } from "./useSessionStorageState";
import {
  getSearchState,
  LSSearchParams,
  searchWithParameters,
  SearchWithParametersResponse,
} from "../pages/search/ls/LSSearch";
import { useBranchLimits } from "../BranchLimits";

interface PrevNextLink {
  disabled: boolean;
  onClick: () => void;
}

interface BackToLink {
  label: string;
  href: string;
}

function findRouteLabel(
  allRoutes: LS2PacRouteObject[],
  pathname: string
): string {
  const matches = matchRoutes(allRoutes, pathname);
  // matches returns root > parent > child ordered array
  return matches
    ?.slice()
    .reverse()
    .find((m) => m.route.handle?.backToLinkTxt)?.route?.handle?.backToLinkTxt;
}

const isTitleRoute = (pathname: string) =>
  !!matchRoutes(routes, { pathname })?.some((m) => m.route.id === "title");

const isSearchResultsRoute = (resolved: Path) => {
  const { pathname } = resolved || {};
  return !!matchRoutes(routes, { pathname })?.some(
    (m) =>
      m.route.id === "advancedSearchResults" ||
      m.route.id === "basicSearchResults"
  );
};

interface TitleNavigationContextType {
  backToLink: BackToLink | null;
  prev: PrevNextLink;
  next: PrevNextLink;
}

export const TitleNavigationContext =
  createContext<TitleNavigationContextType>(null);

export function useTitleNavigation() {
  const context = useContext(TitleNavigationContext);

  if (!context) {
    throw new Error(
      "useTitleNavigation must be used within a TitleNavigationProvider"
    );
  }

  return context;
}

function updateRoutePage(base: Path, page: number) {
  const params = new URLSearchParams(base.search);
  params.set("page", String(page));
  return createPath({
    pathname: base.pathname,
    hash: base.hash,
    search: params.toString(),
  });
}

interface Temp extends SearchWithParametersResponse {
  state: LSSearchParams;
}

export function TitleNavigationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // During a hard refresh, we should check if the link matches...before rehydrating
  const { titleId } = useParams<{ titleId: string }>();
  const id = titleId ? Number(titleId) : null;
  const currentLocation = useLocation();
  const navigation = useNavigation();
  const { configLimitedBranchIds } = useBranchLimits();
  const { state, location } = navigation;
  const navigate = useNavigate();
  const [backToLink, setBackToLink] =
    useSessionStorageState<BackToLink>("backToLink");
  const resolvedPath = useResolvedPath(backToLink?.href);

  const prevLocRef = useRef(currentLocation);
  const pendingIndex = useRef<number | undefined>();

  const isSearchContext = useMemo(() => {
    if (!backToLink) return false;
    return isSearchResultsRoute(resolvedPath);
  }, [backToLink, resolvedPath]);

  const [search, setSearch] = useState<Temp>();

  const index = useMemo(() => {
    if (!search?.resources || !id) {
      return null;
    }
    return search.resources.findIndex((r) => r.id === id);
  }, [search, id]);

  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);

  useEffect(() => {
    if (!backToLink || !isSearchResultsRoute(resolvedPath)) return;
    (async () => {
      setLoadingSearch(true);
      const state = getSearchState(new URLSearchParams(resolvedPath.search));
      const response = await searchWithParameters(
        state,
        configLimitedBranchIds
      );
      if (pendingIndex.current != null) {
        const resources = response.resources;
        const clampedIndex = Math.max(
          0,
          Math.min(pendingIndex.current, resources.length - 1)
        );
        const { id } = resources[clampedIndex];
        pendingIndex.current = null;
        navigate(`/resource/${id}`);
      }
      setLoadingSearch(false);
      setSearch({
        state,
        ...response,
      });
    })();
  }, [backToLink, resolvedPath, configLimitedBranchIds, navigate]);

  // TODO handle index invalidation due to tagging

  useEffect(() => {
    const prev = prevLocRef.current;
    const curr = currentLocation;

    // navigated away from the title route, clear back to link
    if (isTitleRoute(prev.pathname) && !isTitleRoute(curr.pathname)) {
      // now that the new page is rendered, safe to clear
      setBackToLink(undefined);
    }

    prevLocRef.current = curr;
  }, [currentLocation, setBackToLink]);

  useEffect(() => {
    if (state !== "idle") {
      if (
        isTitleRoute(location.pathname) &&
        !isTitleRoute(currentLocation.pathname)
      ) {
        // navigating to titles
        // set back to link
        const href = createPath(currentLocation);
        const label = findRouteLabel(routes, currentLocation.pathname);
        setBackToLink({
          label,
          href,
        });

        // this fires twice because of the redirect
        // once for /resource/:titleId and once for /resource/:titleId/details
        // if deep linking to a specific tab it will only fire once
        // not an issue in this case, but something to be aware of
      }
    }
  }, [state, location, currentLocation]);

  const onClickNext = useCallback(() => {
    if (!search || index == null) {
      return;
    }
    const { resources } = search;

    const nextIndex = index + 1;

    if (nextIndex >= resources.length) {
      if (search.state.page >= search.pages) return;
      pendingIndex.current = 0;
      setBackToLink(({ label }) => {
        const page = search.state.page + 1;
        return {
          label,
          href: updateRoutePage(resolvedPath, page),
        };
      });
      // fetch the next page, update backToLink
      return;
    }

    const { id: nextId } = resources[nextIndex];
    navigate(`/resource/${nextId}`);
  }, [search, index, resolvedPath, setBackToLink, navigate]);
  const onClickPrev = useCallback(() => {
    if (!search || index == null) {
      return;
    }
    const { resources } = search;

    const prevIndex = index - 1;

    if (prevIndex < 0) {
      if (search.state.page <= 1) return;
      pendingIndex.current = search.pageSize - 1;
      // request the previous page, update the back to link in storage
      // set to navigate to the last index of the response
      setBackToLink(({ label }) => {
        const page = search.state.page - 1;
        return {
          label,
          href: updateRoutePage(resolvedPath, page),
        };
      });
      return;
    }

    const { id: nextId } = resources[prevIndex];
    navigate(`/resource/${nextId}`);
  }, [search, index, resolvedPath, setBackToLink, navigate]);
  const prevDisabled = useMemo(() => {
    if (!search || index == null || loadingSearch) {
      return true;
    }

    return index === 0 && search.state.page <= 1;
  }, [search, index, loadingSearch]);

  const nextDisabled = useMemo(() => {
    if (!search || index == null || loadingSearch) {
      return true;
    }

    // are we at the last index of the last page?
    return (
      search.state.page === search.pages && index >= search.resources.length - 1
    );
  }, [search, index, loadingSearch]);

  const contextValue: TitleNavigationContextType = {
    backToLink,
    prev: isSearchContext
      ? {
          disabled: prevDisabled,
          onClick: onClickPrev,
        }
      : undefined,
    next: isSearchContext
      ? {
          disabled: nextDisabled,
          onClick: onClickNext,
        }
      : undefined,
  };

  return (
    <TitleNavigationContext.Provider value={contextValue}>
      {children}
    </TitleNavigationContext.Provider>
  );
}
